import { Menu, MenuOpen } from '@mui/icons-material'
import { Box, IconButton, Stack } from '@mui/material'
import Image from 'next/image'
import Link from 'next/link'
import React, { type ReactNode } from 'react'

export const SIDEBAR_WIDTH = 240

export type MenuItemType = {
  title: string
  Icon: React.ElementType
  href: string
  selected: boolean
  count?: number
  /** Label pill rendered next to the title, e.g. "New". Not rendered while the sidebar is collapsed. */
  badge?: string
  children?: readonly MenuItemType[]
}

type SidebarUncontrolledProps = {
  isSidebarExpanded: boolean
  onToggleExpanded: (value: boolean) => void
  /** Replaces the logo and collapse toggle. */
  header?: ReactNode
  children: ReactNode
}

export const SidebarUncontrolled = ({ isSidebarExpanded, onToggleExpanded, header, children }: SidebarUncontrolledProps) => (
  <Stack
    justifyContent="space-between"
    sx={{
      bgcolor: 'bg.surface.default',
      width: isSidebarExpanded ? SIDEBAR_WIDTH : 70,
      borderRight: 'stroke.default',
      borderRightColor: 'border.subtle',
    }}
  >
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      data-testid="sidebar-header"
      sx={{
        minHeight: 51,
        backgroundColor: 'bg.surface.default',
        borderBottom: 'stroke.default',
        borderColor: 'border.subtle',
        px: 2,
      }}
    >
      {header ?? (
        <>
          {isSidebarExpanded && (
            <Box sx={{ cursor: 'pointer', pt: 1, pl: 4 }}>
              <Link href="/" passHref>
                <Image src="/logo-dark.svg" alt="Omnea" height={18} width={99} priority />
              </Link>
            </Box>
          )}
          <IconButton
            onClick={() => onToggleExpanded(!isSidebarExpanded)}
            sx={{ ...(isSidebarExpanded ? {} : { mx: 'auto', width: '100%', borderRadius: 1 }) }}
          >
            {isSidebarExpanded ? <MenuOpen sx={{ width: 18 }} /> : <Menu sx={{ width: 18 }} />}
          </IconButton>
        </>
      )}
    </Stack>
    <Box
      sx={{
        flexGrow: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {
          width: 8,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: (theme) => theme.palette.bg.neutral.strong,
          borderRadius: 8,
        },
      }}
    >
      {children}
    </Box>
  </Stack>
)
